/*
 * Global functions - Core page initialization and navigation
 */
/* global global, Gryphon, page_state, mainNav, click_back, click_next, click_skip, shield, shieldRecorded, isDeviceDataInProgress */

import { report } from '../../../services/report';
import { tagLoaderError } from '../../../features/questions/common/telemetry';
import { seedHiddenAnswers } from '../answer-sync';

// Import split modules to register their global.* functions
import './navigation.js';
import './scale.js';
import './shield.js';
import './timers.js';
import './form.js';
import './page.js';
import './device.js';
import './utils.js';

// Mock console.log if it doesn't exist (IE).
if (typeof console === 'undefined') {
  global.console = {
    log: function () {},
  };
}

/*
Disabled due to lack of support on backend
$.ajaxSetup({
  beforeSend: function (xhr) {
    xhr.setRequestHeader('Accepts-Version', window.apiVersion);
  },
});
*/



/**
 * Connects widgets or template to the page based on current page state or API response.
 *
 * @global
 * @function connect_widgets_or_template
 * @param {Object} res - The API response object containing initialization state
 * @param {Object} [res.init_state] - Initial state data from the API
 * @param {Array<Object>} [res.init_state.renderables] - Array of widget configuration objects
 * @param {Object} res.init_state.renderables[].question - The question parameter for the widget
 * @param {string} res.init_state.renderables[].view - The view parameter for the widget
 * @param {*} [res.init_state.template] - Template data to load if no widgets are available
 * @returns {void}
 *
 * @description
 * This function binds widget or template loading to the 'after_content_loaded_native' event.
 * It prioritizes widgets over templates when both are available.
 */
global.connect_widgets_or_template = function (res) {
  var renderables = page_state.renderables || res.init_state.renderables;
  var template = page_state.template || res.init_state.template;

  $(page_state).on('after_content_loaded_native', function () {

    (async () => {
      try {
        // Widgets
        if (renderables && renderables.length > 0) {
          const list = renderables.filter(e=> e.view !== 'free-text');
          const failed = [];
          const renderFailed = [];
          // `loadMs` for this page's `Next` page action (RPI-1138); kept on
          // this page's state, even if the next page has loaded by then
          const loadState = page_state;
          const start = Date.now();
          // Download every widget's chunk first, so the order below does not
          // depend on which download lands first
          await Promise.all(list.map((r, i) =>
            Gryphon.widgets.Widget.preload(r.view, r.question)
              .catch(e => {
                failed[i] = tagLoaderError(e, 'widgetLoadError', r.question);
              })
          ));
          // Construct in page order; a widget whose chunk failed is skipped
          // (it cannot construct; its import already reported the error)
          const built = [];
          for (const [i, r] of list.entries()) {
            if (failed[i]) continue;
            try {
              built.push([await Gryphon.construct_widget(r.question, r.view), r.question]);
            } catch (e) {
              failed[i] = tagLoaderError(e, 'widgetLoadError', r.question);
            }
          }
          // Render in page order; each widget's `ready` wait overlaps the next
          await Promise.all(built.map(([w, q], i) =>
            Gryphon.render_widget(w, q).catch(e => {
              renderFailed[i] = tagLoaderError(e, 'customQSLError', q, w._renderPath);
            })
          ));
          loadState.widgets_load_ms = Date.now() - start;
          // The other widgets still render; then the first failure skips the tail.
          // Prefer an error that is not a ChunkLoadError: those were already reported on import
          const errors = failed.concat(renderFailed);
          const firstError = errors.find(e => e && e.name !== 'ChunkLoadError') || errors.find(Boolean);
          if (firstError) {
            throw firstError;
          }
        }

        // Template
        else if (template) {
          try {
            await Gryphon.load_template(template);
          } catch (e) {
            throw tagLoaderError(e, 'customQSLError');
          }
        }
        // Widgets have rendered, so their server-rendered hidden inputs exist
        // and can be mirrored into the AnswerStore. Complements the
        // response-side seed in `answer-seed.js`; see that module's SCOPE note.
        seedHiddenAnswers();
        set_nav_button_visibility(res);
        // A customer's handler: its own label, not the loader's
        try {
          Gryphon.events.emit('after_content_loaded');
        } catch (e) {
          report.logError(tagLoaderError(e, 'customQSLError'));
        }
      } catch (err) {
        const error = tagLoaderError(err, 'widgetLoadError');
        if (error.name === 'ChunkLoadError') {
          error.cause = 'lazyLoadingError';
        }

        if (error.cause === 'lazyLoadingError') {
          // Instead of rethrowing the error, we display what is happening since we already throw in the import wrapper
          console.error('Widget loading failed:', error);
        } else {
          console.error(error)
          report.logError(error);
        }

      }

    })();

  });
};

